<!--
  CommsHistoryPanel — compact embeddable comms history for a single entity.

  Props:
  userId      (String) — filter by userId
  reservationId (String) — filter by reservationId
  restaurantId  (String) — filter by restaurantId
  groupId       (String) — filter by groupId
  email         (String) — filter by recipient email (payload.to)
  compact       (Boolean) — use extra-dense layout (default false)

  Usage:
  <CommsHistoryPanel :userId="user._id" />
  <CommsHistoryPanel :reservation-id="reservation._id" />

  Shows the last 20 comms for the entity with inline status icons,
  click-to-view modal, and a link to the full dashboard with filter applied.
-->
<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import axiosIns from '@/plugins/axios'

const props = defineProps({
  userId: { type: String, default: '' },
  reservationId: { type: String, default: '' },
  restaurantId: { type: String, default: '' },
  groupId: { type: String, default: '' },
  email: { type: String, default: '' },
  compact: { type: Boolean, default: false },
})

const loading = ref(false)
const rows = ref([])
const total = ref(0)
const page = ref(1)
const limit = 20
const maxVisible = 200

// View modal (shared logic with the full dashboard)
const viewDialog = ref(false)
const viewRow = ref(null)

const statusColors = {
  sent: 'success',
  delivered: 'success',
  pending: 'info',
  processing: 'info',
  failed: 'error',
  dead: 'error',
  render_failed: 'deep-purple',
  bounced: 'error',
  complained: 'deep-orange',
  suppressed: 'warning',
}

const statusIcons = {
  sent: 'mdi-check-circle',
  delivered: 'mdi-check-all',
  pending: 'mdi-clock-outline',
  processing: 'mdi-loading',
  failed: 'mdi-alert-circle',
  dead: 'mdi-skull',
  render_failed: 'mdi-code-braces-box',
  bounced: 'mdi-email-remove',
  complained: 'mdi-thumb-down',
  suppressed: 'mdi-cancel',
}

const filterParams = computed(() => {
  const p = { page: page.value, limit }
  if (props.userId) p.userId = props.userId
  if (props.reservationId) p.reservationId = props.reservationId
  if (props.restaurantId) p.restaurantId = props.restaurantId
  if (props.groupId) p.groupId = props.groupId
  if (props.email) p.email = props.email
  
  return p
})

const fullDashboardUrl = computed(() => {
  const base = '/admin-comms'
  const parts = []
  if (props.userId) parts.push(`userId=${encodeURIComponent(props.userId)}`)
  if (props.reservationId) parts.push(`reservationId=${encodeURIComponent(props.reservationId)}`)
  if (props.restaurantId) parts.push(`restaurantId=${encodeURIComponent(props.restaurantId)}`)
  if (props.groupId) parts.push(`groupId=${encodeURIComponent(props.groupId)}`)
  if (props.email) parts.push(`email=${encodeURIComponent(props.email)}`)
  
  return parts.length ? `${base}?${parts.join('&')}` : base
})

async function fetchRows() {
  loading.value = true
  try {
    const res = await axiosIns.get('/admin/comms/messages', { params: filterParams.value })
    rows.value = res.data.data.docs
    total.value = res.data.data.total
  } catch (_err) {
    rows.value = []
    total.value = 0
  } finally {
    loading.value = false
  }
}

function openView(row) {
  viewRow.value = row
  viewDialog.value = true
}

function onPageChange(newPage) {
  page.value = newPage
  fetchRows()
}

function formatDate(d) {
  if (!d) return '—'
  const date = new Date(d)
  
  return date.toLocaleString()
}

function recipient(row) {
  if (row.payload?.to) return row.payload.to
  
  return row.audienceKey || '—'
}

function payloadPreview(payload) {
  if (!payload) return '—'
  if (payload.html) return '[HTML email]'
  if (payload.slackText) return payload.slackText.slice(0, 80)
  if (payload.inAppPayload) return JSON.stringify(payload.inAppPayload).slice(0, 80)
  
  return JSON.stringify(payload).slice(0, 80)
}

// Refetch when filter props change
watch(
  () => [props.userId, props.reservationId, props.restaurantId, props.groupId, props.email],
  () => {
    page.value = 1
    fetchRows()
  },
)

onMounted(fetchRows)
</script>

<template>
  <div class="comms-history-panel">
    <div class="d-flex align-center mb-2">
      <span class="text-subtitle-2 text-medium-emphasis">Comms History</span>
      <VSpacer />
      <VBtn
        size="x-small"
        variant="text"
        :href="fullDashboardUrl"
        target="_self"
        class="text-caption"
      >
        Open in full dashboard
        <VIcon
          size="12"
          class="ml-1"
        >
          mdi-open-in-new
        </VIcon>
      </VBtn>
    </div>

    <VProgressLinear
      v-if="loading"
      indeterminate
      color="primary"
      class="mb-2"
    />

    <div
      v-if="!loading && rows.length === 0"
      class="text-caption text-medium-emphasis py-2"
    >
      No comms found for this entity.
    </div>

    <div v-else>
      <VList
        :density="compact ? 'compact' : 'default'"
        class="pa-0"
      >
        <VListItem
          v-for="row in rows"
          :key="row._id"
          :class="compact ? 'px-0 py-1' : 'px-0'"
          @click="openView(row)"
        >
          <template #prepend>
            <VIcon
              :color="statusColors[row.status] || 'grey'"
              :icon="statusIcons[row.status] || 'mdi-circle-outline'"
              size="16"
              class="mr-2"
            />
          </template>
          <VListItemTitle class="text-body-2">
            {{ row.event }}
            <span class="text-medium-emphasis ml-1 text-caption">{{ row.channel }}</span>
          </VListItemTitle>
          <VListItemSubtitle class="text-caption">
            {{ recipient(row) }} · {{ formatDate(row.createdAt) }}
          </VListItemSubtitle>
          <template #append>
            <VChip
              :color="statusColors[row.status] || 'grey'"
              size="x-small"
              label
            >
              {{ row.status }}
            </VChip>
          </template>
        </VListItem>
      </VList>

      <div
        v-if="total > limit"
        class="d-flex align-center justify-space-between mt-2"
      >
        <span class="text-caption text-medium-emphasis">
          Showing {{ rows.length }} of {{ Math.min(total, maxVisible) }}
          <span v-if="total > maxVisible"> (open dashboard for full history)</span>
        </span>
        <div>
          <VBtn
            size="x-small"
            variant="text"
            :disabled="page === 1"
            @click="onPageChange(page - 1)"
          >
            <VIcon>mdi-chevron-left</VIcon>
          </VBtn>
          <span class="text-caption mx-1">{{ page }}</span>
          <VBtn
            size="x-small"
            variant="text"
            :disabled="page * limit >= Math.min(total, maxVisible)"
            @click="onPageChange(page + 1)"
          >
            <VIcon>mdi-chevron-right</VIcon>
          </VBtn>
        </div>
      </div>
    </div>

    <!-- View detail modal (same as full dashboard) -->
    <VDialog
      v-model="viewDialog"
      max-width="700"
    >
      <VCard v-if="viewRow">
        <VCardTitle>Message Detail</VCardTitle>
        <VCardText>
          <VRow dense>
            <VCol cols="6">
              <strong>ID:</strong> {{ viewRow._id }}
            </VCol>
            <VCol cols="6">
              <strong>Status:</strong>
              <VChip
                :color="statusColors[viewRow.status] || 'grey'"
                size="small"
                label
                class="ml-1"
              >
                {{ viewRow.status }}
              </VChip>
            </VCol>
            <VCol cols="6">
              <strong>Event:</strong> {{ viewRow.event }}
            </VCol>
            <VCol cols="6">
              <strong>Channel:</strong> {{ viewRow.channel }}
            </VCol>
            <VCol cols="6">
              <strong>Template:</strong> {{ viewRow.templateName || '—' }}
            </VCol>
            <VCol cols="6">
              <strong>Attempts:</strong> {{ viewRow.attempts }}
            </VCol>
            <VCol cols="6">
              <strong>Created:</strong> {{ formatDate(viewRow.createdAt) }}
            </VCol>
            <VCol cols="6">
              <strong>Sent:</strong> {{ formatDate(viewRow.sentAt) }}
            </VCol>
          </VRow>

          <div
            v-if="viewRow.renderError"
            class="mt-3"
          >
            <strong class="text-error">Render Error:</strong>
            <pre class="detail-pre">{{ viewRow.renderError }}</pre>
          </div>
          <div
            v-if="viewRow.lastError"
            class="mt-3"
          >
            <strong>Last Error:</strong>
            <pre class="detail-pre">{{ viewRow.lastError }}</pre>
          </div>
          <div
            v-if="viewRow.payload"
            class="mt-3"
          >
            <strong>Payload Preview:</strong>
            <div class="text-body-2 mt-1">
              {{ payloadPreview(viewRow.payload) }}
            </div>
            <div
              v-if="viewRow.payload.to"
              class="mt-1"
            >
              <strong>To:</strong> {{ viewRow.payload.to }}
            </div>
            <div
              v-if="viewRow.payload.subject"
              class="mt-1"
            >
              <strong>Subject:</strong> {{ viewRow.payload.subject }}
            </div>
          </div>
          <div
            v-if="viewRow.context && Object.keys(viewRow.context).length"
            class="mt-3"
          >
            <strong>Raw Context:</strong>
            <pre class="detail-pre">{{ JSON.stringify(viewRow.context, null, 2).slice(0, 1500) }}</pre>
          </div>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn @click="viewDialog = false">
            Close
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>

<style scoped>
.comms-history-panel {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  padding: 12px;
}
.detail-pre {
  background: #f5f5f5;
  padding: 8px;
  border-radius: 4px;
  font-size: 11px;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 180px;
  overflow-y: auto;
}
</style>
